import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, first, map } from 'rxjs';
import {
  LOGIN,
  LOGIN_RESET,
  LOGIN_SUCCESS,
  LOGOUT,
} from '../store/login/login.actions';
import { LoginPayload } from '../interfaces/login-payload';
import {
  selectEsito,
  selectServizi,
  selectToken,
} from '../store/login/login.selector';
import { ILoginState } from '../store/login/login';
import {
  SET_IS_LOTTOMATICA,
  RETRIEVE_CONFIG,
  RETRIEVE_NOTIFICATIONS,
  RETRIEVE_SURVEY,
  RETRIEVE_REGEX_DHL,
  RETRIEVE_EXPIRED_OBJECT,
} from '../store/config/config.actions';
import {
  selectBarcodeAmz,
  selectApplicationVersion,
  selectBasepath,
  selectCaptchaLength,
  selectExpiredObjectConfig,
  selectIsLottomatica,
  selectIsMaintainance,
  selectRegexDHL,
  selectSeparatorRegexDHL,
  selectSurvey,
  selectTentativeLogin,
  selectNumberOfResultsPerPage,
  selectHomepageLink,
  selectGiorniAnticipoFerie,
  selectShowSurvey,
  selectIsNotificationPresent,
  selectDebounceTime,
  selectTimerForLoginFocus,
  selectIsRedirectEnable,
} from '../store/config/config.selector';
import {
  selectResponseCodiceBustaBXL,
  selectResponseConfirmClientAcceptance,
  selectResponseConfirmClientAcceptanceDatamatrix,
  selectResponseVerificaAccettazioneStampaPaperless,
  selectResponseConsegnaPacco,
  selectResponseEtichettaBXLLottomatica,
  selectResponseEtichettaBXLWebApp,
  selectResponsePaccoSmarrito,
  selectResponseParcelSale,
  selectResponseRifiutaPacco,
  selectResponseVerifyDelivery,
  selectResponseVerifyDeliveryNoPin,
  selectResponseVerifySale,
} from '../store/client-activities/client-activities.selector';
import { IParcelData } from '../interfaces/response-verify-delivery';
import {
  RETRIEVE_CONFIRM_CLIENT_ACCEPTANCE,
  RETRIEVE_CONFIRM_CLIENT_ACCEPTANCE_DATAMATRIX,
  RETRIEVE_CONFIRM_CLIENT_ACCEPTANCE_DATAMATRIX_SUCCESS,
  RETRIEVE_CONFIRM_CLIENT_ACCEPTANCE_SUCCESS,
  RETRIEVE_CONFIRM_CODICE_BUSTA,
  RETRIEVE_CONFIRM_CODICE_BUSTA_SUCCESS,
  RETRIEVE_CONSEGNA_PACCO,
  RETRIEVE_CONSEGNA_PACCO_SUCCESS,
  RETRIEVE_ETICHETTE_LOTTOMATICA,
  RETRIEVE_ETICHETTE_LOTTOMATICA_SUCCESS,
  RETRIEVE_ETICHETTE_WEBAPP,
  RETRIEVE_ETICHETTE_WEBAPP_SUCCESS,
  RETRIEVE_PACCO_SMARRITO,
  RETRIEVE_PACCO_SMARRITO_SUCCESS,
  RETRIEVE_PARCEL_SALE,
  RETRIEVE_PARCEL_SALE_SUCCESS,
  RETRIEVE_RIFIUTA_PACCO,
  RETRIEVE_RIFIUTA_PACCO_SUCCESS,
  RETRIEVE_VERIFY_DELIVERY,
  RETRIEVE_VERIFY_DELIVERY_NO_PIN,
  RETRIEVE_VERIFY_DELIVERY_NO_PIN_SUCCESS,
  RETRIEVE_VERIFY_DELIVERY_SUCCESS,
  RETRIEVE_VERIFY_SALE,
  RETRIEVE_VERIFY_SALE_SUCCESS,
  VERIFICA_ACCETTAZIONE_STAMPA_PAPERLESS,
} from '../store/client-activities/client-activities.actions';
import {
  IPayloadRifiutaPacco,
  IResponseRifiutaPacco,
} from '../interfaces/rifiuta-pacco';
import {
  ResponseAccettazioneDatamatrix,
  ResponseConfirmClientAcceptance,
  ResponseConfirmCodiceBusta,
  ResponseEtichettaLottomatica,
  ResponseEtichettaWebApp,
} from '../interfaces/response-confirm-client-acceptance';
import {
  IResponsePaccoSmarrito,
  IPayloadPaccoSmarrito,
} from '../interfaces/pacco-smarrito';
import {
  IParcelSale,
  IPayloadParcelSale,
} from '../interfaces/response-parcel-sale';
import { ISaleData } from '../interfaces/response-verify-sale';
import { BarcodeAmzConfig } from '../interfaces/config';
import { IResponseRetrieveBolla } from '../interfaces/response-retrieve-bolla';
import {
  RETRIEVE_ACQUISISCI_BOLLA,
  RETRIEVE_ACQUISISCI_BOLLA_SUCCESS,
  RETRIEVE_ANNULLA_BOLLA,
  RETRIEVE_ANNULLA_BOLLA_SUCCESS,
  RETRIEVE_BOLLA,
  RETRIEVE_BOLLA_POST,
  RETRIEVE_BOLLA_POST_NO_PALMARE,
  RETRIEVE_BOLLA_POST_NO_PALMARE_SUCCESS,
  RETRIEVE_BOLLA_POST_SUCCESS,
  RETRIEVE_BOLLA_SUCCESS,
  RETRIEVE_CONSEGNA_SENZA_PALMARE,
  RETRIEVE_CONSEGNA_SENZA_PALMARE_SUCCESS,
  RETRIEVE_MAGAZZINO_CONTATORI,
  RETRIEVE_MAGAZZINO_CONTATORI_SUCCESS,
  RETRIEVE_MAGAZZINO_PICKUP,
  RETRIEVE_MAGAZZINO_PICKUP_DELETE,
  RETRIEVE_MAGAZZINO_PICKUP_DELETE_SUCCESS,
  RETRIEVE_MAGAZZINO_PICKUP_SUCCESS,
  RETRIEVE_STAMPA_SCONTRINO_RITIRO,
  RETRIEVE_STAMPA_SCONTRINO_RITIRO_SUCCESS,
} from '../store/courier-activities/courier-activities.actions';
import {
  IPayloadEliminaMagazzinoPickup,
  IResponseMagazzinoPickup,
} from '../interfaces/response-magazzino-pickup';
import {
  selectAnnullaBolla,
  selectConsegnaSenzaPalmare,
  selectResponseAcquisisciBolla,
  selectResponseBolla,
  selectResponseBollaPost,
  selectResponseBollaPostNoPalmare,
  selectResponseMagazzinoContatori,
  selectResponseMagazzinoPickup,
  selectResponseMagazzinoPickupDelete,
  selectResponseStampaScontrinoRitiro,
} from '../store/courier-activities/courier-activities.selector';
import { IBollaPost, IBollaPostResponse } from '../interfaces/bolla-post';
import { IMagazzinoContatoriResponse } from '../interfaces/magazzino-contatori';
import {
  IParamsStampaScontrinoRitiro,
  IStampaScontrinoRitiroResponse,
} from '../interfaces/stampa-scontrino-ritiro';
import {
  IAcquisisciBollaPayload,
  IAcquisisciBollaResponse,
  IAnnullaBolla,
} from '../interfaces/acquisisci-bolla';
import { IPacchiInGiacenzaResponse } from '../interfaces/response-pacchi-giacenza';
import {
  selectBeniContoDeposito,
  selectBoxToCheck,
  selectChangeStatusVerifica,
  selectChiudiScatola,
  selectChiusuraScatoleBxl,
  selectEsportaFileMail,
  selectEsportaFileXls,
  selectExistsScatolaFlyer,
  selectMovimentazioneCreateEvent,
  selectMovimentazioneList,
  selectPacchiInGiacenza,
  selectScatolaNonDisponibile,
  selectStoricoAnnualeContoDesposito,
  selectStoricoContoDeposito,
} from '../store/package-warehouse/package-warehouse.selector';
import {
  BENI_CONTO_DEPOSITO,
  BENI_CONTO_DEPOSITO_SUCCESS,
  CHANGE_STATUS_VERIFICA,
  CHANGE_STATUS_VERIFICA_SUCCESS,
  CHIUDI_SCATOLA,
  CHIUDI_SCATOLA_SUCCESS,
  CHIUSURA_SCATOLE_BXL,
  CHIUSURA_SCATOLE_BXL_SUCCESS,
  ESPORTA_FILE_MAIL,
  ESPORTA_FILE_MAIL_SUCCESS,
  ESPORTA_FILE_XLS,
  ESPORTA_FILE_XLS_SUCCESS,
  EXISTS_SCATOLA_FLYER,
  EXISTS_SCATOLA_FLYER_SUCCESS,
  MOVIMENTAZIONE_CREATE_EVENT,
  MOVIMENTAZIONE_CREATE_EVENT_SUCCESS,
  MOVIMENTAZIONE_LIST,
  MOVIMENTAZIONE_LIST_SUCCESS,
  PACCHI_IN_GIACENZA,
  PACCHI_IN_GIACENZA_SUCCESS,
  RETRIVE_BOX_TO_CHECK,
  RETRIVE_BOX_TO_CHECK_SUCCESS,
  SCATOLA_NON_DISPONIBILE,
  SCATOLA_NON_DISPONIBILE_SUCCESS,
  STORICO_ANNUALE_CONTO_DEPOSITO,
  STORICO_ANNUALE_CONTO_DEPOSITO_SUCCESS,
  STORICO_OGGETTO_CONTO_DEPOSITO,
  STORICO_OGGETTO_CONTO_DEPOSITO_SUCCESS,
} from '../store/package-warehouse/package-warehouse.actions';
import {
  ICollectPointPayload,
  IMovimentazioneListPayload,
  IMovimentazioneListResponse,
} from '../interfaces/movimentazione-list';
import { IMovimentazioneMailResponse } from '../interfaces/movimentazione-mail';
import { IVerificaMagazzinoPacchi } from '../interfaces/response-verifica-magazzino-pacchi';
import { IBeniContoDeposito } from '../interfaces/beni-conto-deposito';
import {
  IPayloadScatolaNonDisponibile,
  IResponseScatolaNonDisponibile,
} from '../interfaces/scatola-non-disponibile';
import {
  IPayloadChangeStatusVerifica,
  IResponseChangeStatusVerifica,
} from '../interfaces/change-status-verifica';
import { IResponseExistsScatolaFlyer } from '../interfaces/exists-scatola-flyer';
import {
  IPayloadChiudiScatola,
  IResponseChiudiScatola,
} from '../interfaces/chiudi-scatola';
import {
  IPayloadChiusuraScatoleBxl,
  IResponseChiusuraScatoleBxl,
} from '../interfaces/chiusura-scatole-bxl';
import {
  IPayloadStoricoAnnualeContoDeposito,
  IPayloadStoricoOggettoContoDeposito,
  IResponseStoricoOggettoContoDeposito,
} from '../interfaces/storico-oggetto-conto-deposito';
import { selectMessages } from '../store/messages/messages.selector';
import { RESET_MESSAGE } from '../store/messages/messages.actions';
import {
  selectApprovvigionamentoOrdine,
  selectApprovvigionamentoOrdineFinale,
  selectApprovvigionamentoProdotti,
  selectCatalogoAppartenenza,
  selectCatalogoAppartenenzaReso,
  selectCreaReso,
  selectEsportaStoricoOrdini,
  selectGenerateBarcodeScatolaReso,
  selectMailStoricoOrdini,
  selectProdottiReso,
  selectProductsBoxAcceptance,
  selectStoricoOrdiniMateriali,
} from '../store/consumable-warehouse/consumable-warehouse.selector';
import {
  ICatalogoAppartenenzaResoResponse,
  ICatalogoAppartenenzaResponse,
  IProdottiResoResponse,
  IProductsBoxAcceptance,
} from '../interfaces/catalogo-appartenenza';
import {
  APPROVVIGIONAMENTO_ORDINE,
  APPROVVIGIONAMENTO_ORDINE_FINALE,
  APPROVVIGIONAMENTO_ORDINE_FINALE_SUCCESS,
  APPROVVIGIONAMENTO_ORDINE_SUCCESS,
  APPROVVIGIONAMENTO_PRODOTTI,
  APPROVVIGIONAMENTO_PRODOTTI_SUCCESS,
  CATALOGO_APPARTENENZA,
  CATALOGO_APPARTENENZA_RESO,
  CATALOGO_APPARTENENZA_RESO_SUCCESS,
  CATALOGO_APPARTENENZA_SUCCESS,
  CREA_RESO,
  CREA_RESO_SUCCESS,
  ESPORTA_STORICO_ORDINI,
  ESPORTA_STORICO_ORDINI_SUCCESS,
  GENERTE_BARCODE_SCATOLA_RESO,
  GENERTE_BARCODE_SCATOLA_RESO_SUCCESS,
  MAIL_STORICO_ORDINI,
  MAIL_STORICO_ORDINI_SUCCESS,
  PRODOTTI_RESO,
  PRODOTTI_RESO_SUCCESS,
  RETRIEVE_PRODUCTS_BOX_ACCEPTANCE,
  RETRIEVE_PRODUCTS_BOX_ACCEPTANCE_SUCCESS,
  STORICO_ORDINI_MATERIALI,
  STORICO_ORDINI_MATERIALI_SUCCESS,
} from '../store/consumable-warehouse/consumable-warehouse.actions';
import {
  IPayloadApprovvigionamentoOrdine,
  IPayloadApprovvigionamentoOrdineFinale,
  IResponseApprovvigionamentoOrdine,
  IResponseApprovvigionamentoOrdineFinale,
  IResponseApprovvigionamentoProdotti,
} from '../interfaces/approvvigionamento-ordine';
import { IdReturnsActionDropdownEnum } from '../enums/consumable-warehouse.enum';
import {
  IPayloadGenerateBarcodeReso,
  IResponseGenerateBarcodeReso,
} from '../interfaces/generate-barcode-scatola-reso';
import {
  ANAGRAFICA,
  ANAGRAFICA_SUCCESS,
  CAMBIO_PASSWORD,
  CAMBIO_PASSWORD_SUCCESS,
  GET_BOARD_LIST,
  GET_BOARD_LIST_SUCCESS,
  ORARI_CHIUSURE,
  ORARI_CHIUSURE_PUT,
  ORARI_CHIUSURE_PUT_SUCCESS,
  ORARI_CHIUSURE_SUCCESS,
} from '../store/punto-poste-management/punto-poste-management.actions';
import { IResponseAnagrafica } from '../interfaces/anagrafica';
import {
  selectBoardList,
  selectResponseAnagrafica,
  selectResponseCambioPassword,
  selectResponseOrariChiusure,
  selectResponsePutOrariChiusure,
} from '../store/punto-poste-management/punto-poste-management.selector';
import { IResponseCreaReso } from '../interfaces/crea-reso';
import {
  IPayloadCambioPassword,
  IResponseCambioPassword,
} from '../interfaces/cambio-password';
import {
  IPayloadPutOrari,
  IResponseOrariChiusure,
} from '../interfaces/orari-chiusure';
import {
  IPayloadStoricoOrdiniMateriali,
  IResponseStoricoOrdiniMateriali,
} from '../interfaces/storico-ordini-materiali';
import { IPayloadBacheca, IResponseBacheca } from '../interfaces/bacheca';
import { IPayloadMovimentazioneCreateEvent, IResponseMovimentazioneCreateEvent } from '../interfaces/movimentazione-create-event';
import { IPayloadVerificaAccettazioneStampaPaperless, IResponseVerificaAccettazioneStampaPaperless } from '../interfaces/verifica-accettazione-stampa-paperless';

@Injectable({
  providedIn: 'root',
})
export class FacadeService {
  /**
   * GETTER
   */

  get guard() {
    return {
      selectToken$: ((): Observable<string> =>
        this.store$.select(selectToken))(),
      selectIsLottomatica$: ((): Observable<boolean> =>
        this.store$.select(selectIsLottomatica))(),
      selectIsMaintainance$: ((): Observable<boolean | undefined> =>
        this.store$.select(selectIsMaintainance))(),
    };
  }

  get generic() {
    return {
      selectBasepath$: ((): Observable<string> =>
        this.store$.select(selectBasepath).pipe(
          first((basepath) => Boolean(basepath)),
          map((config) => config as string)
        ))(),
      selectExpiredObjectConfig$: ((): Observable<{
        oggettiScadutiOggi: boolean;
        oggettiScadutiPrimaDiOggi: boolean;
        giornataLavorativa: boolean;
        frequenzaPopupOggettiScaduti: number;
      }> => this.store$.select(selectExpiredObjectConfig))(),
      selectHomePageLink$: ((): Observable<string | undefined> =>
        this.store$.select(selectHomepageLink))(),
      selectDebounceTime$: ((): Observable<number> =>
        this.store$.select(selectDebounceTime))(),
      selectIsNotificationPresent$: ((): Observable<{
        isNotificationPresent: boolean;
        messaggioHeader: string | null;
        nuovaComunicazione: string | null;
      }> => this.store$.select(selectIsNotificationPresent))(),
      selectTimerToFocus$: ((): Observable<number> => this.store$.select(selectTimerForLoginFocus))(),
      selectIsRedirectEnabeld$: ((): Observable<Boolean> => this.store$.select(selectIsRedirectEnable))(),
    };
  }

  get header() {
    return {
      selectSurvey$: ((): Observable<string> =>
        this.store$.select(selectSurvey))(),
      selectShowSurvey$: ((): Observable<boolean> =>
        this.store$.select(selectShowSurvey))(),
    };
  }

  get footer() {
    return {
      selectApplicationVersion$: ((): Observable<string> =>
        this.store$.select(selectApplicationVersion))(),
    };
  }

  get login() {
    return {
      selectConfig$: ((): Observable<number[]> =>
        combineLatest([
          this.store$.select(selectCaptchaLength),
          this.store$.select(selectTentativeLogin),
        ]).pipe(
          first((config) => config.every((val: number | undefined) => !!val)),
          map((config) => config as number[])
        ))(),
      selectEsito$: ((): Observable<string> =>
        this.store$.select(selectEsito))(),
      selectServizi$: ((): Observable<string[]> =>
        this.store$.select(selectServizi))(),
    };
  }

  get clientDelivery() {
    return {
      selectResponseVerifyDelivery$: ((): Observable<IParcelData | null> =>
        this.store$.select(selectResponseVerifyDelivery))(),
      selectResponseVerifyDeliveryNoPin$: ((): Observable<IParcelData | null> =>
        this.store$.select(selectResponseVerifyDeliveryNoPin))(),
      selectResponseRifiutaPacco$:
        ((): Observable<IResponseRifiutaPacco | null> =>
          this.store$.select(selectResponseRifiutaPacco))(),
      selectResponseConsegnaPacco$:
        ((): Observable<IResponseRifiutaPacco | null> =>
          this.store$.select(selectResponseConsegnaPacco))(),
      selectResponsePaccoSmarrito$:
        ((): Observable<IResponsePaccoSmarrito | null> =>
          this.store$.select(selectResponsePaccoSmarrito))(),
    };
  }

  get parcelSales() {
    return {
      selectResponseVerifySale$: ((): Observable<ISaleData | null> =>
        this.store$.select(selectResponseVerifySale))(),
      selectResponseParcelSale$: ((): Observable<IParcelSale | null> =>
        this.store$.select(selectResponseParcelSale))(),
    };
  }

  get clientAcceptance() {
    return {
      selectIsLottomatica$: ((): Observable<boolean> =>
        this.store$.select(selectIsLottomatica))(),
      selectRegexDHL$: ((): Observable<string> =>
        this.store$.select(selectRegexDHL))(),
      selectSeparatorRegexDHL$: ((): Observable<string> =>
        this.store$.select(selectSeparatorRegexDHL))(),
      selectBarcodeAmz$: ((): Observable<BarcodeAmzConfig> =>
        this.store$.select(selectBarcodeAmz))(),
      selectResponseConfirmClientAcceptance$:
        ((): Observable<ResponseConfirmClientAcceptance | null> =>
          this.store$.select(selectResponseConfirmClientAcceptance))(),
      selectResponseConfirmClientAcceptanceDatamatrix$:
        ((): Observable<ResponseAccettazioneDatamatrix | null> =>
          this.store$.select(
            selectResponseConfirmClientAcceptanceDatamatrix
          ))(),
      selectResponseEtichettaBXLWebapp$:
        ((): Observable<ResponseEtichettaWebApp | null> =>
          this.store$.select(selectResponseEtichettaBXLWebApp))(),
      selectResponseEtichettaBXLLottomatica$:
        ((): Observable<ResponseEtichettaLottomatica | null> =>
          this.store$.select(selectResponseEtichettaBXLLottomatica))(),
      selectResponseConfirmCodiceBustaBXL$:
        ((): Observable<ResponseConfirmCodiceBusta | null> =>
          this.store$.select(selectResponseCodiceBustaBXL))(),
        selectResponseVerificaAccettazioneStampaPaperless$:
        ((): Observable<IResponseVerificaAccettazioneStampaPaperless | null> =>
          this.store$.select(selectResponseVerificaAccettazioneStampaPaperless))(),
    };
  }

  get courierDelivery() {
    return {
      selectResponseBolla$: ((): Observable<IResponseRetrieveBolla | null> =>
        this.store$.select(selectResponseBolla))(),
      selectResponseMagazzinoPickup$:
        ((): Observable<IResponseMagazzinoPickup | null> =>
          this.store$.select(selectResponseMagazzinoPickup))(),
      selectResponseBollaPost$: ((): Observable<IBollaPostResponse | null> =>
        this.store$.select(selectResponseBollaPost))(),
      selectResponseMagazzinoContatori$:
        ((): Observable<IMagazzinoContatoriResponse | null> =>
          this.store$.select(selectResponseMagazzinoContatori))(),
      selectResponseStampaScontrinoRitiro$:
        ((): Observable<IStampaScontrinoRitiroResponse | null> =>
          this.store$.select(selectResponseStampaScontrinoRitiro))(),
      selectResponseAcquisisciBolla$:
        ((): Observable<IAcquisisciBollaResponse | null> =>
          this.store$.select(selectResponseAcquisisciBolla))(),
      selectResponseBollaPostNoPalmare$:
        ((): Observable<IBollaPostResponse | null> =>
          this.store$.select(selectResponseBollaPostNoPalmare))(),
      selectResponseMagazzinoPickupDelete$:
        ((): Observable<IResponseMagazzinoPickup | null> =>
          this.store$.select(selectResponseMagazzinoPickupDelete))(),
      selectAnnullaBolla$: ((): Observable<IAnnullaBolla | null> =>
        this.store$.select(selectAnnullaBolla))(),
      selectConsegnaSenzaPalmare$: ((): Observable<IAnnullaBolla | null> =>
        this.store$.select(selectConsegnaSenzaPalmare))(),
    };
  }

  get packageWarehouse() {
    return {
      selectPacchiInGiacenza$:
        ((): Observable<IPacchiInGiacenzaResponse | null> =>
          this.store$.select(selectPacchiInGiacenza))(),
      selectMovimentazioneList$:
        ((): Observable<IMovimentazioneListResponse | null> =>
          this.store$.select(selectMovimentazioneList))(),
      selectEsportaFileXls$: ((): Observable<any> =>
        this.store$.select(selectEsportaFileXls))(),
      selectEsportaFileMail$:
        ((): Observable<IMovimentazioneMailResponse | null> =>
          this.store$.select(selectEsportaFileMail))(),
      selectBoxToCheck$: ((): Observable<IVerificaMagazzinoPacchi | null> =>
        this.store$.select(selectBoxToCheck))(),
      selectNumberOfResultsPerPage$: ((): Observable<number> =>
        this.store$.select(selectNumberOfResultsPerPage))(),
      selectBeniContoDeposito$: ((): Observable<IBeniContoDeposito | null> =>
        this.store$.select(selectBeniContoDeposito))(),
      selectScatolaNonDisponibile$:
        ((): Observable<IResponseScatolaNonDisponibile | null> =>
          this.store$.select(selectScatolaNonDisponibile))(),
      selectChangeStatusVerifica$:
        ((): Observable<IResponseChangeStatusVerifica | null> =>
          this.store$.select(selectChangeStatusVerifica))(),
      selectExistsScatolaFlyer$:
        ((): Observable<IResponseExistsScatolaFlyer | null> =>
          this.store$.select(selectExistsScatolaFlyer))(),
      selectChiudiScatola$: ((): Observable<IResponseChiudiScatola | null> =>
        this.store$.select(selectChiudiScatola))(),
      selectChiusuraScatoleBxl$:
        ((): Observable<IResponseChiusuraScatoleBxl | null> =>
          this.store$.select(selectChiusuraScatoleBxl))(),
      selectStoricoContoDesposito$:
        ((): Observable<IResponseStoricoOggettoContoDeposito | null> =>
          this.store$.select(selectStoricoContoDeposito))(),
      selectStoricoAnnualeContoDesposito$: ((): Observable<string | null> =>
        this.store$.select(selectStoricoAnnualeContoDesposito))(),
      selectMovimentazioneCreateEvent$: ((): Observable<IResponseMovimentazioneCreateEvent | null> =>
        this.store$.select(selectMovimentazioneCreateEvent))(),
    };
  }

  get consumableWarehouse() {
    return {
      selectCatalogoAppartenenza$:
        ((): Observable<ICatalogoAppartenenzaResponse | null> =>
          this.store$.select(selectCatalogoAppartenenza))(),
      selectApprovvigionamentoOrdine$:
        ((): Observable<IResponseApprovvigionamentoOrdine | null> =>
          this.store$.select(selectApprovvigionamentoOrdine))(),
      selectApprovvigionamentoProdotti$:
        ((): Observable<IResponseApprovvigionamentoProdotti | null> =>
          this.store$.select(selectApprovvigionamentoProdotti))(),
      selectApprovvigionamentoOrdineFinale$:
        ((): Observable<IResponseApprovvigionamentoOrdineFinale | null> =>
          this.store$.select(selectApprovvigionamentoOrdineFinale))(),
      selectCatalogoAppartenenzaReso$:
        ((): Observable<ICatalogoAppartenenzaResoResponse | null> =>
          this.store$.select(selectCatalogoAppartenenzaReso))(),
      selectProdottiReso$: ((): Observable<IProdottiResoResponse | null> =>
        this.store$.select(selectProdottiReso))(),
      selectGenerateBarcodeScatolaReso$:
        ((): Observable<IResponseGenerateBarcodeReso | null> =>
          this.store$.select(selectGenerateBarcodeScatolaReso))(),
      selectCreaReso$: ((): Observable<IResponseCreaReso | null> =>
        this.store$.select(selectCreaReso))(),
      selectStoricoOrdiniMateriali$:
        ((): Observable<IResponseStoricoOrdiniMateriali | null> =>
          this.store$.select(selectStoricoOrdiniMateriali))(),
      selectEsportaStoricoOrdini$: ((): Observable<any> =>
        this.store$.select(selectEsportaStoricoOrdini))(),
      selectMailStoricoOrdini$:
        ((): Observable<IResponseStoricoOrdiniMateriali | null> =>
          this.store$.select(selectMailStoricoOrdini))(),
      selectProductsBoxAcceptance$:
        ((): Observable<IProductsBoxAcceptance | null> =>
          this.store$.select(selectProductsBoxAcceptance))(),
    };
  }

  get puntoPosteManagement() {
    return {
      selectResponseAnagrafica$: ((): Observable<IResponseAnagrafica | null> =>
        this.store$.select(selectResponseAnagrafica))(),
      selectResponseCambioPassword$:
        ((): Observable<IResponseCambioPassword | null> =>
          this.store$.select(selectResponseCambioPassword))(),
      selectResponseOrariChiusure$:
        ((): Observable<IResponseOrariChiusure | null> =>
          this.store$.select(selectResponseOrariChiusure))(),
      selectResponseOrariChiusurePut$:
        ((): Observable<IResponseOrariChiusure | null> =>
          this.store$.select(selectResponsePutOrariChiusure))(),
      selectGiorniAnticipoFerie$: ((): Observable<number> =>
        this.store$.select(selectGiorniAnticipoFerie))(),
      selectBoardList$: ((): Observable<IResponseBacheca | null> =>
        this.store$.select(selectBoardList))(),
    };
  }

  get messages() {
    return {
      selectMessages$: ((): Observable<string | null> =>
        this.store$.select(selectMessages))(),
    };
  }

  constructor(private store$: Store) {}

  /**
   * DISPATCH
   */
  submitLogin(body: LoginPayload): void {
    this.store$.dispatch(LOGIN({ body }));
  }

  submitLogout(): void {
    this.store$.dispatch(LOGOUT());
  }

  successLogin(responseLogin: ILoginState): void {
    this.store$.dispatch(LOGIN_SUCCESS({ responseLogin }));
  }

  retriveNotification(): void {
    this.store$.dispatch(RETRIEVE_NOTIFICATIONS());
  }
  retriveSurvey(): void {
    this.store$.dispatch(RETRIEVE_SURVEY());
  }

  retriveRegexDhl(): void {
    this.store$.dispatch(RETRIEVE_REGEX_DHL());
  }

  retriveExpiredObject(): void {
    this.store$.dispatch(RETRIEVE_EXPIRED_OBJECT());
  }

  retrieveConfig(token: string): void {
    this.store$.dispatch(RETRIEVE_CONFIG({ token }));
  }

  logout(): void {
    this.store$.dispatch(LOGIN_RESET());
  }

  setIsLottomatica(isLottomatica: boolean): void {
    this.store$.dispatch(SET_IS_LOTTOMATICA({ isLottomatica }));
  }

  retrieveVerifyDelivery(pin: string): void {
    this.store$.dispatch(RETRIEVE_VERIFY_DELIVERY({ pin }));
  }
  resetVerifyDelivery(): void {
    this.store$.dispatch(
      RETRIEVE_VERIFY_DELIVERY_SUCCESS({ responseVerifyDelivery: null })
    );
  }

  retrieveVerifyDeliveryNoPin(parcelCode: string): void {
    this.store$.dispatch(RETRIEVE_VERIFY_DELIVERY_NO_PIN({ parcelCode }));
  }
  resetVerifyDeliveryNoPin(): void {
    this.store$.dispatch(
      RETRIEVE_VERIFY_DELIVERY_NO_PIN_SUCCESS({
        responseVerifyDeliveryNoPin: null,
      })
    );
  }

  retrieveRifiutaPacco(payload: IPayloadRifiutaPacco): void {
    this.store$.dispatch(RETRIEVE_RIFIUTA_PACCO({ payload }));
  }

  resetRifiutaPacco(): void {
    this.store$.dispatch(
      RETRIEVE_RIFIUTA_PACCO_SUCCESS({ responseRifiutaPacco: null })
    );
  }

  verificaAccettazioneStampaPaperless(payload: IPayloadVerificaAccettazioneStampaPaperless): void {
    this.store$.dispatch(VERIFICA_ACCETTAZIONE_STAMPA_PAPERLESS({ payload }));
  }

  retrieveConsegnaPacco(payload: IPayloadRifiutaPacco): void {
    this.store$.dispatch(RETRIEVE_CONSEGNA_PACCO({ payload }));
  }

  resetConsegnaPacco(): void {
    this.store$.dispatch(
      RETRIEVE_CONSEGNA_PACCO_SUCCESS({ responseConsegnaPacco: null })
    );
  }
  retrieveVerifySale(code: string): void {
    this.store$.dispatch(RETRIEVE_VERIFY_SALE({ code }));
  }

  retrievePaccoSmarrito(payload: IPayloadPaccoSmarrito): void {
    this.store$.dispatch(RETRIEVE_PACCO_SMARRITO({ payload }));
  }
  resetPaccoSmarrito(): void {
    this.store$.dispatch(
      RETRIEVE_PACCO_SMARRITO_SUCCESS({ responsePaccoSmarrito: null })
    );
  }

  resetVerifySale(): void {
    this.store$.dispatch(
      RETRIEVE_VERIFY_SALE_SUCCESS({ responseVerifySale: null })
    );
  }

  retrieveParcelSale(payload: IPayloadParcelSale): void {
    this.store$.dispatch(RETRIEVE_PARCEL_SALE({ payload }));
  }

  resetParcelSale(): void {
    this.store$.dispatch(
      RETRIEVE_PARCEL_SALE_SUCCESS({ responseParcelSale: null })
    );
  }

  retrieveConfirmClientAcceptance(boxCode: string): void {
    this.store$.dispatch(RETRIEVE_CONFIRM_CLIENT_ACCEPTANCE({ boxCode }));
  }

  resetConfirmClientAcceptance(): void {
    this.store$.dispatch(
      RETRIEVE_CONFIRM_CLIENT_ACCEPTANCE_SUCCESS({
        responseConfirmClientAcceptance: null,
      })
    );
  }

  retrieveConfirmClientAcceptanceDatamatrix(dataMatrix: string): void {
    this.store$.dispatch(
      RETRIEVE_CONFIRM_CLIENT_ACCEPTANCE_DATAMATRIX({ dataMatrix })
    );
  }
  resetConfirmClientAcceptanceDatamatrix(): void {
    this.store$.dispatch(
      RETRIEVE_CONFIRM_CLIENT_ACCEPTANCE_DATAMATRIX_SUCCESS({
        responseConfirmClientAcceptanceDatamatrix: null,
      })
    );
  }

  retrieveEtichettaBXLWebapp(): void {
    this.store$.dispatch(RETRIEVE_ETICHETTE_WEBAPP());
  }
  resetEtichettaBXLWebapp(): void {
    this.store$.dispatch(
      RETRIEVE_ETICHETTE_WEBAPP_SUCCESS({ responseEtichettaWebApp: null })
    );
  }

  retrieveEtichettaBXLLottomatica(): void {
    this.store$.dispatch(RETRIEVE_ETICHETTE_LOTTOMATICA());
  }
  resetEtichettaBXLLottomatica(): void {
    this.store$.dispatch(
      RETRIEVE_ETICHETTE_LOTTOMATICA_SUCCESS({
        responseEtichettaLottomatica: null,
      })
    );
  }

  retrieveCodiceBustaBXL(codiceBusta: string): void {
    this.store$.dispatch(RETRIEVE_CONFIRM_CODICE_BUSTA({ codiceBusta }));
  }
  resetCodiceBustaBXL(): void {
    this.store$.dispatch(
      RETRIEVE_CONFIRM_CODICE_BUSTA_SUCCESS({
        responseConfirmCodiceBusta: null,
      })
    );
  }

  retrieveBolla(): void {
    this.store$.dispatch(RETRIEVE_BOLLA());
  }
  resetRetrieveBolla(): void {
    this.store$.dispatch(
      RETRIEVE_BOLLA_SUCCESS({ responseRetrieveBolla: null })
    );
  }

  retrieveMagazzinoPickup(): void {
    this.store$.dispatch(RETRIEVE_MAGAZZINO_PICKUP());
  }
  resetRetrieveMagazzinoPickup(): void {
    this.store$.dispatch(
      RETRIEVE_MAGAZZINO_PICKUP_SUCCESS({ responseMagazzinoPickup: null })
    );
  }

  retrieveBollaPost(payloadBollaPost: IBollaPost): void {
    this.store$.dispatch(RETRIEVE_BOLLA_POST({ payloadBollaPost }));
  }
  resetRetrieveBollaPost(): void {
    this.store$.dispatch(
      RETRIEVE_BOLLA_POST_SUCCESS({ responseRetrieveBollaPost: null })
    );
  }

  retrieveMagazzinoContatori(): void {
    this.store$.dispatch(RETRIEVE_MAGAZZINO_CONTATORI());
  }
  resetRetrieveMagazzinoContatori(): void {
    this.store$.dispatch(
      RETRIEVE_MAGAZZINO_CONTATORI_SUCCESS({ responseMagazzinoContatori: null })
    );
  }

  retrieveStampaScontrinoRitiro(params: IParamsStampaScontrinoRitiro): void {
    this.store$.dispatch(RETRIEVE_STAMPA_SCONTRINO_RITIRO({ params }));
  }
  resetStampaScontrinoRitiro(): void {
    this.store$.dispatch(
      RETRIEVE_STAMPA_SCONTRINO_RITIRO_SUCCESS({
        responseStampaScontrinoRitiro: null,
      })
    );
  }

  retrieveAcquisisciBolla(payload: IAcquisisciBollaPayload): void {
    this.store$.dispatch(RETRIEVE_ACQUISISCI_BOLLA({ payload }));
  }
  resetAcquisisciBolla(): void {
    this.store$.dispatch(
      RETRIEVE_ACQUISISCI_BOLLA_SUCCESS({ responseAcquisisciBolla: null })
    );
  }

  retrieveBollaPostNoPalmare(payload: IBollaPost): void {
    this.store$.dispatch(RETRIEVE_BOLLA_POST_NO_PALMARE({ payload }));
  }
  resetRetrieveBollaPostNoPalmare(): void {
    this.store$.dispatch(
      RETRIEVE_BOLLA_POST_NO_PALMARE_SUCCESS({
        responseBollaPostNoPalmare: null,
      })
    );
  }

  retrieveMagazzinoPickupDelete(payload: IPayloadEliminaMagazzinoPickup): void {
    this.store$.dispatch(RETRIEVE_MAGAZZINO_PICKUP_DELETE({ payload }));
  }
  resetRetrieveMagazzinoPickupDelete(): void {
    this.store$.dispatch(
      RETRIEVE_MAGAZZINO_PICKUP_DELETE_SUCCESS({
        responseDeleteMagazzinoPickup: null,
      })
    );
  }

  retrieveAnnullaBolla(): void {
    this.store$.dispatch(RETRIEVE_ANNULLA_BOLLA());
  }
  resetAnnullaBolla(): void {
    this.store$.dispatch(
      RETRIEVE_ANNULLA_BOLLA_SUCCESS({ responseAnnullaBolla: null })
    );
  }

  retrieveConsegnaSenzaPalmare(): void {
    this.store$.dispatch(RETRIEVE_CONSEGNA_SENZA_PALMARE());
  }
  resetConsegnaSenzaPalmare(): void {
    this.store$.dispatch(
      RETRIEVE_CONSEGNA_SENZA_PALMARE_SUCCESS({
        responseConsegnaSenzaPalmare: null,
      })
    );
  }

  retrievePacchiInGiacenza(): void {
    this.store$.dispatch(PACCHI_IN_GIACENZA());
  }
  resetPacchiInGiacenza(): void {
    this.store$.dispatch(
      PACCHI_IN_GIACENZA_SUCCESS({ responsePacchiInGiacenza: null })
    );
  }

  retrieveMovimentazioneList(payload: IMovimentazioneListPayload): void {
    this.store$.dispatch(MOVIMENTAZIONE_LIST({ payload }));
  }
  resetMovimentazioneList(): void {
    this.store$.dispatch(
      MOVIMENTAZIONE_LIST_SUCCESS({ responseMovimentazione: null })
    );
  }

  retrieveEsportaFileXls(payload: IMovimentazioneListPayload): void {
    this.store$.dispatch(ESPORTA_FILE_XLS({ payload }));
  }
  resetEsportaFileXls(): void {
    this.store$.dispatch(
      ESPORTA_FILE_XLS_SUCCESS({ responseEsportaXls: null })
    );
  }

  retrieveEsportaMail(payload: ICollectPointPayload): void {
    this.store$.dispatch(ESPORTA_FILE_MAIL({ payload }));
  }
  resetEsportaMail(): void {
    this.store$.dispatch(
      ESPORTA_FILE_MAIL_SUCCESS({ responseEsportaMail: null })
    );
  }

  retrieveBoxToCheck(): void {
    this.store$.dispatch(RETRIVE_BOX_TO_CHECK());
  }
  resetBoxToCheck(): void {
    this.store$.dispatch(
      RETRIVE_BOX_TO_CHECK_SUCCESS({ responseVerificaBox: null })
    );
  }

  retrieveBeniContoDeposito(): void {
    this.store$.dispatch(BENI_CONTO_DEPOSITO());
  }
  resetBeniContoDeposito(): void {
    this.store$.dispatch(
      BENI_CONTO_DEPOSITO_SUCCESS({ responseBeniContoDeposito: null })
    );
  }

  retrieveScatolaNonDisponibile(payload: IPayloadScatolaNonDisponibile): void {
    this.store$.dispatch(SCATOLA_NON_DISPONIBILE({ payload }));
  }
  resetScatolaNonDisponibile(): void {
    this.store$.dispatch(
      SCATOLA_NON_DISPONIBILE_SUCCESS({ responseScatolaNonDisponibile: null })
    );
  }

  retrieveChangeStatusVerifica(payload: IPayloadChangeStatusVerifica): void {
    this.store$.dispatch(CHANGE_STATUS_VERIFICA({ payload }));
  }
  resetChangeStatusVerifica(): void {
    this.store$.dispatch(
      CHANGE_STATUS_VERIFICA_SUCCESS({ responseChangeStatusVerifica: null })
    );
  }

  retrieveExistsScatolaFlyer(code: string): void {
    this.store$.dispatch(EXISTS_SCATOLA_FLYER({ code }));
  }
  resetExistsScatolaFlyer(): void {
    this.store$.dispatch(
      EXISTS_SCATOLA_FLYER_SUCCESS({ responseExistsScatolaFlyer: null })
    );
  }

  retrieveChiudiScatola(payload: IPayloadChiudiScatola): void {
    this.store$.dispatch(CHIUDI_SCATOLA({ payload }));
  }
  resetChiudiScatola(): void {
    this.store$.dispatch(
      CHIUDI_SCATOLA_SUCCESS({ responseChiudiScatola: null })
    );
  }

  retrieveChiusuraScatoleBxl(payload: IPayloadChiusuraScatoleBxl): void {
    this.store$.dispatch(CHIUSURA_SCATOLE_BXL({ payload }));
  }
  resetChiusuraScatoleBxl(): void {
    this.store$.dispatch(
      CHIUSURA_SCATOLE_BXL_SUCCESS({ responseChiusuraScatoleBxl: null })
    );
  }

  retrieveStoricoContoDeposito(
    payload: IPayloadStoricoOggettoContoDeposito
  ): void {
    this.store$.dispatch(STORICO_OGGETTO_CONTO_DEPOSITO({ payload }));
  }
  resetStoricoContoDeposito(): void {
    this.store$.dispatch(
      STORICO_OGGETTO_CONTO_DEPOSITO_SUCCESS({
        responseStoricoContoDeposito: null,
      })
    );
  }

  retrieveStoricoAnnualeContoDeposito(
    payload: IPayloadStoricoAnnualeContoDeposito
  ): void {
    this.store$.dispatch(STORICO_ANNUALE_CONTO_DEPOSITO({ payload }));
  }
  resetStoricoAnnualeContoDeposito(): void {
    this.store$.dispatch(
      STORICO_ANNUALE_CONTO_DEPOSITO_SUCCESS({
        responseStoricoAnnualeContoDeposito: null,
      })
    );
  }

  retrieveCatalogoAppartenenza(): void {
    this.store$.dispatch(CATALOGO_APPARTENENZA());
  }
  resetCatalogoAppartenenza(): void {
    this.store$.dispatch(
      CATALOGO_APPARTENENZA_SUCCESS({ responseCatalogoAppartenenza: null })
    );
  }

  retrieveApprovvigionamentoOrdine(
    payload: IPayloadApprovvigionamentoOrdine
  ): void {
    this.store$.dispatch(APPROVVIGIONAMENTO_ORDINE({ payload }));
  }
  resetApprovvigionamentoOrdine(): void {
    this.store$.dispatch(
      APPROVVIGIONAMENTO_ORDINE_SUCCESS({
        responseApprovvigionamentoOrdine: null,
      })
    );
  }

  retrieveApprovvigionamentoProdotti(
    payload: IPayloadApprovvigionamentoOrdine
  ): void {
    this.store$.dispatch(APPROVVIGIONAMENTO_PRODOTTI({ payload }));
  }
  resetApprovvigionamentoProdotti(): void {
    this.store$.dispatch(
      APPROVVIGIONAMENTO_PRODOTTI_SUCCESS({
        responseApprovvigionamentoProdotti: null,
      })
    );
  }

  retrieveApprovvigionamentoOrdineFinale(
    payload: IPayloadApprovvigionamentoOrdineFinale
  ): void {
    this.store$.dispatch(APPROVVIGIONAMENTO_ORDINE_FINALE({ payload }));
  }
  resetApprovvigionamentoOrdineFinale(): void {
    this.store$.dispatch(
      APPROVVIGIONAMENTO_ORDINE_FINALE_SUCCESS({
        responseApprovvigionamentoOrdineFinale: null,
      })
    );
  }

  retrieveCatalogoAppartenenzaReso(): void {
    this.store$.dispatch(CATALOGO_APPARTENENZA_RESO());
  }
  resetCatalogoAppartenenzaReso(): void {
    this.store$.dispatch(
      CATALOGO_APPARTENENZA_RESO_SUCCESS({
        responseCatalogoAppartenenzaReso: null,
      })
    );
  }

  retrieveProdottiReso(returnType: IdReturnsActionDropdownEnum): void {
    this.store$.dispatch(PRODOTTI_RESO({ returnType }));
  }
  resetProdottiReso(): void {
    this.store$.dispatch(PRODOTTI_RESO_SUCCESS({ responseProdottiReso: null }));
  }

  retrieveGenerateBarcodeReso(payload: IPayloadGenerateBarcodeReso): void {
    this.store$.dispatch(GENERTE_BARCODE_SCATOLA_RESO({ payload }));
  }
  resetGenerateBarcodeReso(): void {
    this.store$.dispatch(
      GENERTE_BARCODE_SCATOLA_RESO_SUCCESS({
        responseGenerateBarcodeScatolaReso: null,
      })
    );
  }

  retrieveCreaReso(payload: IPayloadGenerateBarcodeReso): void {
    this.store$.dispatch(CREA_RESO({ payload }));
  }
  resetCreaReso(): void {
    this.store$.dispatch(CREA_RESO_SUCCESS({ responseCreaReso: null }));
  }

  retrieveAnagrafica(): void {
    this.store$.dispatch(ANAGRAFICA());
  }
  resetAnagrafica(): void {
    this.store$.dispatch(ANAGRAFICA_SUCCESS({ responseAnagrafica: null }));
  }

  retrieveCambioPassword(payload: IPayloadCambioPassword): void {
    this.store$.dispatch(CAMBIO_PASSWORD({ payload }));
  }
  resetCambioPassword(): void {
    this.store$.dispatch(
      CAMBIO_PASSWORD_SUCCESS({ responseCambioPassword: null })
    );
  }

  retrieveOrariChiusure(): void {
    this.store$.dispatch(ORARI_CHIUSURE());
  }
  resetOrariChiusure(): void {
    this.store$.dispatch(
      ORARI_CHIUSURE_SUCCESS({ responseOrariChiusure: null })
    );
  }

  retrieveOrariChiusurePut(payload: IPayloadPutOrari): void {
    this.store$.dispatch(ORARI_CHIUSURE_PUT({ payload }));
  }
  resetOrariChiusurePut(): void {
    this.store$.dispatch(
      ORARI_CHIUSURE_PUT_SUCCESS({ responseOrariChiusurePut: null })
    );
  }

  retrieveStoricoOrdiniMateriali(
    payload: IPayloadStoricoOrdiniMateriali
  ): void {
    this.store$.dispatch(STORICO_ORDINI_MATERIALI({ payload }));
  }
  resetStoricoOrdiniMateriali(): void {
    this.store$.dispatch(
      STORICO_ORDINI_MATERIALI_SUCCESS({ responseStoricoOrdiniMateriali: null })
    );
  }

  retrieveEsportaStoricoOrdini(payload: IPayloadStoricoOrdiniMateriali): void {
    this.store$.dispatch(ESPORTA_STORICO_ORDINI({ payload }));
  }
  resetEsportaStoricoOrdini(): void {
    this.store$.dispatch(
      ESPORTA_STORICO_ORDINI_SUCCESS({ responseEsportaStoricoOrdini: null })
    );
  }

  retrieveMailStoricoOrdini(payload: IPayloadStoricoOrdiniMateriali): void {
    this.store$.dispatch(MAIL_STORICO_ORDINI({ payload }));
  }
  resetMailStoricoOrdini(): void {
    this.store$.dispatch(
      MAIL_STORICO_ORDINI_SUCCESS({ responseMailStoricoOrdini: null })
    );
  }

  retrieveBachecaList(payload: IPayloadBacheca): void {
    this.store$.dispatch(GET_BOARD_LIST({ payload }));
  }
  resetBachecaList(): void {
    this.store$.dispatch(GET_BOARD_LIST_SUCCESS({ responseBacheca: null }));
  }

  resetMessages(): void {
    this.store$.dispatch(RESET_MESSAGE());
  }

  retrieveProductsBoxAcceptance(): void {
    this.store$.dispatch(RETRIEVE_PRODUCTS_BOX_ACCEPTANCE());
  }
  resetProductsBoxAcceptance(): void {
    this.store$.dispatch(RETRIEVE_PRODUCTS_BOX_ACCEPTANCE_SUCCESS({ responseProductsBoxAcceptance: null }));
  }

  retrieveMovimentazioneCreateEvent(payload: IPayloadMovimentazioneCreateEvent): void {
    this.store$.dispatch(MOVIMENTAZIONE_CREATE_EVENT({ payload }));
  }
  resetMovimentazioneCreateEvent(): void {
    this.store$.dispatch(MOVIMENTAZIONE_CREATE_EVENT_SUCCESS({ responseMovimentazioneCreateEvent: null }));
  }

}
